/**
 * YD Product Sequence 2 Mobile Section
 * 移动端专用的产品序列帧动画区块
 *
 * 功能:
 * - 阶段一: 序列帧 scrub 到全透明
 * - 阶段二: 8 星球滚动 scrub 切换（水平轮播视觉）
 * - 阶段三: 序列帧 Scrub + 标题动画
 */
(function() {
  'use strict';

  // 仅在移动端执行 (< 768px)
  if (window.innerWidth >= 768) {
    return;
  }

  /**
   * 视口尺寸变化分发器
   * 地址栏伸缩这类「仅高度小幅变化」已由 layout/theme.liquid 的 --locked-vh 两阶段策略过滤：
   * 稳定后触摸设备只在宽度 / 横竖屏 / ≥150px 高度变化时改 --locked-vh 并请求一次 safe refresh。
   * 这里不再自己判断阈值、也不再调 YDLockedVH.refresh / ScrollTrigger.refresh（早期版本在此双重
   * refresh，每次约 400ms），只负责在 --locked-vh 变化或宽度/朝向变化时分发回调（canvas 位图重算、
   * 轮播尺寸重算）。回调用 rAF 合并并延后一帧，保证读到的是按新 --locked-vh 排好的布局。
   */
  const viewportHeightManager = {
    lockedHeight: null,
    lastOrientation: null,
    lastWidth: null,
    resizeCallbacks: [],
    _notifyPending: false,

    initialize() {
      this.lockedHeight = window.innerHeight;
      this.lastOrientation = this.getOrientation();
      this.lastWidth = window.innerWidth;
      this.applyHeight();

      // 全局 --locked-vh 变化（横竖屏 / 大幅高度变化）
      window.addEventListener('yd:locked-vh', () => this.notify());
      // 宽度 / 朝向变化兜底（--locked-vh 恰好未变时也要重算布局）
      window.addEventListener('resize', () => this.handleResize());
    },

    getOrientation() {
      return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
    },

    handleResize() {
      const newOrientation = this.getOrientation();
      if (newOrientation !== this.lastOrientation || window.innerWidth !== this.lastWidth) {
        this.lastOrientation = newOrientation;
        this.lastWidth = window.innerWidth;
        this.notify();
      }
      // 仅高度变化（地址栏）：--locked-vh 不变，CSS 盒不变，无需任何处理
    },

    notify() {
      if (this._notifyPending) return;
      this._notifyPending = true;
      requestAnimationFrame(() => {
        this._notifyPending = false;
        this.lockedHeight = window.innerHeight;
        this.resizeCallbacks.forEach(cb => {
          try { cb(); } catch (e) {}
        });
      });
    },

    applyHeight() {
      // CSS 主高度统一用全局 --locked-vh（见 .yd-h-screen 覆写），这里只确保全局值已追平
      if (window.YDLockedVH && typeof window.YDLockedVH.refresh === 'function') {
        window.YDLockedVH.refresh();
      }
    },

    getHeight() {
      return this.lockedHeight;
    },

    // 注册需要在真正 resize 时执行的回调
    onResize(callback) {
      if (typeof callback === 'function') {
        this.resizeCallbacks.push(callback);
      }
    }
  };

  /**
   * 解析滚动高度配置（如 "200svh" → 像素值）
   * 优先：window.YDLockedVH（全局 early-lock 注入）
   * 次优：viewportHeightManager.getHeight()（本模块自己的锁定值）
   * 兜底：window.innerHeight
   * @param {string|number} value - 滚动高度值（如 "200svh" 或具体像素数）
   * @param {number} fallbackMultiplier - value 为 null/undefined/非法时的默认倍数（默认 2，即 200svh）
   */
  function parseScrollHeight(value, fallbackMultiplier = 2) {
    // 统一 vh 源：移动端优先用全局 YDLockedVH（与 early-lock / refresh 同源），
    // 缺失时才回退 section 内 viewportHeightManager / innerHeight。
    // 避免两套 vh 管理器解析不一致导致 ScrollTrigger end 计算偏差。
    const lockedVH = window.YDLockedVH && window.YDLockedVH.value;
    const fallbackVH = viewportHeightManager.getHeight() || window.innerHeight;
    const viewportHeight = (lockedVH && lockedVH > 0) ? lockedVH * 100 : fallbackVH;
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && (value.endsWith('vh') || value.endsWith('svh'))) {
      return (parseFloat(value) / 100) * viewportHeight;
    }
    return viewportHeight * fallbackMultiplier;
  }

  /**
   * 等待 canvas 尺寸稳定（连续 2 帧 getBoundingClientRect().height 一致且非零）后回调。
   * 用于消除"pin 在 canvas 尺寸=0 时建"的错位根因。最多重试 ~2s 兜底。
   */
  function whenCanvasStable(selector, cb) {
    const el = document.querySelector(selector);
    if (!el) { cb(); return; }
    let lastH = -1, stableCount = 0, tries = 0;
    const tick = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0 && h === lastH) {
        stableCount++;
      } else {
        stableCount = 0;
      }
      lastH = h;
      if (stableCount >= 1 || tries++ > 120) { cb(); return; }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /**
   * 图片格式检测工具 (复用自桌面端)
   */
  const imageFormatDetector = {
    _cache: {},

    async isSupported(format) {
      if (this._cache[format] !== undefined) {
        return this._cache[format];
      }

      if (format === 'avif') {
        const result = await this._checkAvifSupport();
        this._cache[format] = result;
        return result;
      }

      return false;
    },

    _checkAvifSupport() {
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img.width > 0 && img.height > 0);
        img.onerror = () => resolve(false);
        img.src = 'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKBzgABogQEAwgMgwYAAAAKAAAAB4A';
        setTimeout(() => resolve(false), 1000);
      });
    }
  };

  /**
   * 星球时间控制器 (移动端简化版 - 单一固定显示元素)
   */
  class PlanetTimeController {
    constructor(planets) {
      this.planets = planets;
      this.planetConfigs = {};
      this.updateInterval = null;
      this.isRunning = false;
      this.activePlanetId = null;

      // 固定显示元素
      this.timeDisplay = null;
    }

    initialize() {
      // 缓存固定显示元素
      this.timeDisplay = document.querySelector('#planet-time-display');

      // 构建星球配置
      for (const planet of this.planets) {
        this.planetConfigs[planet.id] = {
          rotationPeriodHours: planet.rotationPeriodHours
        };
      }
    }

    startTimeUpdate(planetId) {
      if (this.activePlanetId === planetId && this.isRunning) return;

      this.activePlanetId = planetId;
      this.stopTimeUpdate();

      // 立即更新一次
      this.updateTime();

      // 设置定时更新
      this.updateInterval = setInterval(() => this.updateTime(), 1000);
      this.isRunning = true;
    }

    stopTimeUpdate() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
      }
      this.isRunning = false;
    }

    updateTime() {
      if (!this.activePlanetId || !this.timeDisplay) return;

      const planetConfig = this.planetConfigs[this.activePlanetId];
      if (!planetConfig) return;

      const earthTime = new Date();
      const convertedTime = this.convertToPlanetTime(earthTime, planetConfig.rotationPeriodHours);

      this.timeDisplay.textContent = convertedTime;
    }

    convertToPlanetTime(earthTime, rotationPeriodHours) {
      const earthSeconds =
        earthTime.getHours() * 3600 +
        earthTime.getMinutes() * 60 +
        earthTime.getSeconds();

      const earthDaySeconds = 24 * 3600;
      const planetDaySeconds = rotationPeriodHours * 3600;

      const planetSeconds = earthSeconds * (earthDaySeconds / planetDaySeconds);

      const hours = Math.floor(planetSeconds / 3600) % 24;
      const minutes = Math.floor((planetSeconds % 3600) / 60);
      const seconds = Math.floor(planetSeconds % 60);

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }

    dispose() {
      this.stopTimeUpdate();
      this.planetConfigs = {};
      this.activePlanetId = null;
    }
  }

  /**
   * 移动端序列帧加载器：实现见 assets/yd-sequence-loader.js（全站共用）。
   * 按帧范围稀疏加载；单帧失败返回 null；后台加载每批 10 帧串行；就近取帧最多回找 50 帧。
   */
  class MobileSequenceLoader extends window.YDSequenceLoader {
    constructor(config) {
      super({
        basePath: config.framePath,
        avifPath: config.avifPath,
        fallbackPath: config.fallbackPath,
        priorityBase: config.priorityBase || 10000,
        progressMode: 'range',
        onFrameError: 'null',
        continueBatchSize: 10,
        safeSearchLimit: 50
      });
    }
  }

  /**
   * Canvas 渲染器 (复用自桌面端)
   */
  class CanvasRenderer {
    constructor(canvasId) {
      this.canvas = document.querySelector(canvasId);
      if (!this.canvas) {
        throw new Error(`Canvas not found: ${canvasId}`);
      }

      this.ctx = this.canvas.getContext('2d');
      // 记录上次成功计算位图时的 CSS 盒尺寸：盒未变则不重算。
      // 早期版本按「朝向」锁死位图以躲工具栏伸缩，但 CSS 盒跟随 --locked-vh 变化时位图不跟，
      // 画面就被拉伸；现在地址栏伸缩已在 --locked-vh 层被过滤，锁定条件回归真实意图。
      this.lockedCssW = 0;
      this.lockedCssH = 0;
      this.resizeCanvas();

      // 使用 viewportHeightManager 统一管理，只在真正 resize 时才重绘
      viewportHeightManager.onResize(() => this.resizeCanvas());
    }

    resizeCanvas() {
      const rect = this.canvas.getBoundingClientRect();

      // 尺寸为 0（pin 未建/布局未稳）时不锁定，等下次回调重试，避免锁死 0 尺寸
      if (rect.width <= 0 || rect.height <= 0) {
        return;
      }

      const cssW = Math.round(rect.width);
      const cssH = Math.round(rect.height);
      // CSS 盒未变 → 位图不动
      if (cssW === this.lockedCssW && cssH === this.lockedCssH) {
        return;
      }

      // 移动端强制 DPR = 1，桌面端最大 2（性能优化）
      const isMobile = window.innerWidth < 768;
      const dpr = isMobile ? 1 : Math.min(window.devicePixelRatio || 1, 2);

      this.canvas.width = Math.round(rect.width * dpr);
      this.canvas.height = Math.round(rect.height * dpr);

      // canvas.width 赋值已重置 2D context 变换矩阵
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      this.canvasWidth = rect.width;
      this.canvasHeight = rect.height;

      this.lockedCssW = cssW;
      this.lockedCssH = cssH;
    }

    renderFrame(img, opacity = 1) {
      if (!img) return;

      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      // 设置全局透明度
      this.ctx.globalAlpha = opacity;

      const imgAspect = img.width / img.height;
      const canvasAspect = this.canvasWidth / this.canvasHeight;

      let drawWidth, drawHeight, drawX, drawY;

      if (imgAspect > canvasAspect) {
        drawHeight = this.canvasHeight;
        drawWidth = this.canvasHeight * imgAspect;
        drawX = (this.canvasWidth - drawWidth) / 2;
        drawY = 0;
      } else {
        drawWidth = this.canvasWidth;
        drawHeight = this.canvasWidth / imgAspect;
        drawX = 0;
        drawY = (this.canvasHeight - drawHeight) / 2;
      }

      this.ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);

      // 重置透明度
      this.ctx.globalAlpha = 1;
    }

    clear() {
      this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
    }
  }

  /**
   * 阶段一控制器 - 序列帧 scrub + 放大淡出到全黑
   *
   * 交互流程:
   * 1. 序列帧 scrub (0-50帧)
   * 2. 放大 + 淡出到全黑 (最后一帧 scale 1→1.3, opacity 1→0)
   */
  class Phase1Controller {
    constructor(config, loader, renderer) {
      this.config = config;
      this.loader = loader;
      this.renderer = renderer;

      this.totalFrames = config.totalFrames;
      this.totalScrollDistance = 0;

      // 序列帧 scrub 占总滚动的比例（data-config 内固定为 1，整段用于序列帧）
      this.sequenceScrollRatio = config.phase1?.sequenceScrollRatio || 1;

      this.titleElements = {};
      this.currentFrame = 0;
      this.lastRenderedFrame = -1;

      this.scrollTrigger = null;
    }

    // 按当前 vh 解析滚动距离；end 函数 + invalidateOnRefresh 在 refresh 时复用，确保重算
    computeScrollDistance() {
      this.totalScrollDistance = parseScrollHeight(this.config.scrollHeight, 1);
      return this.totalScrollDistance;
    }

    initialize() {
      this.computeScrollDistance();

      // 缓存标题元素
      this.cacheTitleElements();

      // 设置 section 高度
      this.setupScrollSpacer();

      // 尝试渲染第一帧（如果帧已加载）
      this.renderFrame(0, 1);

      // 初始化标题
      this.updateTitles(0);

      // resize（横竖屏切换）时 canvas 物理尺寸重算会清空画面，这里补重渲当前帧。
      // Phase1 的 renderFrame 对相同帧号会短路，故先重置 lastRenderedFrame 强制重画。
      viewportHeightManager.onResize(() => {
        if (this.currentFrame >= 0) {
          const frame = this.currentFrame;
          this.lastRenderedFrame = -1;
          this.renderFrame(frame);
        }
      });

      // 延迟建 pin：交由 YDMobileReady 在首批帧+尺寸就绪后调用 createScrollTrigger()。
      // 协调器未激活（理论上不会，本文件仅移动端跑）时退回原行为即时建。
      if (window.YDMobileReady && window.YDMobileReady.active) {
        window.YDMobileReady.need('seq-p1');
        window.YDMobileReady.onRelease(() => this.createScrollTrigger());
      } else {
        this.createScrollTrigger();
      }
    }

    cacheTitleElements() {
      const titles = this.config.phase1?.titles || [];
      for (const titleConfig of titles) {
        if (titleConfig.id) {
          const el = document.querySelector(`#${titleConfig.id}`);
          if (el) {
            this.titleElements[titleConfig.id] = el;
            // 标题初始状态：如果没有 fadeIn 配置，默认显示
            const initialOpacity = titleConfig.fadeIn ? 0 : 1;
            gsap.set(el, { opacity: initialOpacity });
          }
        }
      }
    }

    setupScrollSpacer() {
      // 不再手动设置高度，由 ScrollTrigger 的 pinSpacing 自动处理
    }

    createScrollTrigger() {
      if (typeof ScrollTrigger === 'undefined') {
        return;
      }

      // 使用 pinSpacing: true 让 ScrollTrigger 自动管理间距
      // refreshPriority 8：低于第一段序列(10)，保证多 pin 自上而下刷新
      // end 用函数式 + invalidateOnRefresh：横竖屏/大幅视口变化时按新 vh 重算滚动距离，
      // 避免 pin 视觉高度（--locked-vh）与滚动距离脱钩造成偏移
      this.scrollTrigger = ScrollTrigger.create({
        trigger: '#seq2-mobile-canvas-wrapper',
        start: 'top top',
        end: () => '+=' + this.computeScrollDistance(),
        pin: '#seq2-mobile-canvas-wrapper',
        pinSpacing: true,
        scrub: 1,
        refreshPriority: 8,
        invalidateOnRefresh: true,
        onUpdate: (self) => this.handleScrollUpdate(self.progress)
      });
    }

    handleScrollUpdate(progress) {
      // sequenceScrollRatio 在 data-config 内固定为 1，整段滚动都用于序列帧 scrub
      const currentFrame = Math.floor(progress * (this.totalFrames - 1));
      this.renderFrame(currentFrame);
      this.updateTitles(currentFrame);
    }

    renderFrame(frameNumber) {
      if (frameNumber === this.lastRenderedFrame) {
        return;
      }
      this.currentFrame = frameNumber;

      const img = this.loader.getFrameSafe(frameNumber);
      if (img) {
        this.renderer.renderFrame(img);
      }
      // 只有目标帧真正画上才记录帧号：画的是回退帧或什么都没画时，帧到位后对同帧号的重绘不能被短路
      this.lastRenderedFrame = this.loader.getFrame(frameNumber) ? frameNumber : -1;
    }

    updateTitles(currentFrame) {
      const titles = this.config.phase1?.titles || [];

      for (const titleConfig of titles) {
        const el = this.titleElements[titleConfig.id];
        if (!el) continue;

        let opacity = 1;

        // 淡入逻辑
        if (titleConfig.fadeIn) {
          const { startFrame, endFrame } = titleConfig.fadeIn;
          if (currentFrame < startFrame) {
            opacity = 0;
          } else if (currentFrame < endFrame) {
            opacity = (currentFrame - startFrame) / (endFrame - startFrame);
          }
        }

        // 淡出逻辑
        if (titleConfig.fadeOut && opacity > 0) {
          const { startFrame, endFrame } = titleConfig.fadeOut;
          if (currentFrame >= endFrame) {
            opacity = 0;
          } else if (currentFrame > startFrame) {
            opacity = 1 - (currentFrame - startFrame) / (endFrame - startFrame);
          }
        }

        gsap.set(el, { opacity: Math.max(0, Math.min(1, opacity)) });
      }
    }

    dispose() {
      if (this.scrollTrigger) {
        this.scrollTrigger.kill();
        this.scrollTrigger = null;
      }
    }
  }


  /**
   * 阶段二控制器 - 手动轮播（swipe + 左右箭头）
   *
   * 替代 PlanetScrubController 的 pin + scrub 模式。
   * section 不再被 pin，固定 1 屏高度内用户手动 swipe 或点击箭头切换 8 颗星球。
   */
  class PlanetCarouselController {
    constructor(config, timeController) {
      this.config = config;
      this.timeController = timeController;

      // 容器和滑块元素
      this.container = document.querySelector('.planet-images-container');
      this.wrapper = document.querySelector('.planet-images-wrapper');
      this.slides = document.querySelectorAll('.planet-slide');

      // 箭头按钮
      this.prevBtn = document.querySelector('#planet-arrow-prev');
      this.nextBtn = document.querySelector('#planet-arrow-next');

      // 固定显示元素
      this.displays = {
        cycle: document.querySelector('#planet-cycle-display'),
        name: document.querySelector('#planet-name-display'),
        lst: document.querySelector('#planet-lst-display'),
        o2: document.querySelector('#planet-o2-display'),
        grav: document.querySelector('#planet-grav-display'),
        dia: document.querySelector('#planet-dia-display')
      };

      // 星球数据
      this.planetsData = [];

      // 状态
      this.currentIndex = 0;
      this.totalSlides = 0;
      this.slideWidth = 0;
      this.isAnimating = false;
      this.touchStartX = 0;
      this.swipeThreshold = 50;
      this.clonesAdded = false;
    }

    initialize() {
      this.loadPlanetsData();
      if (!this.container || !this.wrapper || this.slides.length === 0) return;
      if (this.planetsData.length === 0) return;

      this.totalSlides = Math.min(this.slides.length, this.planetsData.length);

      this.calculateDimensions();
      this.setupLayout();

      this.updateDisplays(0, false);

      const firstPlanet = this.planetsData[0];
      if (firstPlanet) this.timeController.startTimeUpdate(firstPlanet.id);

      // 无限循环：箭头始终可见，一次性设置
      if (this.prevBtn) {
        gsap.set(this.prevBtn, { opacity: 0.6 });
        this.prevBtn.style.pointerEvents = 'auto';
      }
      if (this.nextBtn) {
        gsap.set(this.nextBtn, { opacity: 0.6 });
        this.nextBtn.style.pointerEvents = 'auto';
      }
      this.bindEvents();

      viewportHeightManager.onResize(() => {
        this.calculateDimensions();
        this.setupLayout();
      });
    }

    calculateDimensions() {
      this.slideWidth = this.container.offsetWidth;
    }

    setupLayout() {
      // 克隆首尾做无限循环（如果还没克隆过）
      if (!this.clonesAdded) {
        const totalSlides = this.totalSlides;
        if (totalSlides >= 2) {
          const firstClone = this.slides[0].cloneNode(true);
          const lastClone = this.slides[totalSlides - 1].cloneNode(true);
          firstClone.classList.add('planet-slide-clone');
          lastClone.classList.add('planet-slide-clone');
          // 移除 loading="lazy"：clone img 在 src 已存在的情况下走浏览器缓存
          firstClone.querySelectorAll('img[loading]').forEach((img) => img.removeAttribute('loading'));
          lastClone.querySelectorAll('img[loading]').forEach((img) => img.removeAttribute('loading'));
          // 末尾克隆 = slides[0]（地球）；开头克隆 = slides[7]（海王星）
          this.wrapper.appendChild(firstClone);
          this.wrapper.insertBefore(lastClone, this.wrapper.firstChild);
          this.clonesAdded = true;
        }
      }

      const slideCount = this.totalSlides + 2; // +2 是首尾克隆
      this.wrapper.style.width = `${slideCount * this.slideWidth}px`;

      // 所有 slide（包括克隆的）都设宽度
      const allSlides = this.wrapper.querySelectorAll('.planet-slide, .planet-slide-clone');
      allSlides.forEach(slide => {
        slide.style.width = `${this.slideWidth}px`;
      });

      // 初始位置：让 currentIndex（默认 0=地球）居中，即 wrapper x = -slideWidth（因为前面有一个克隆海王星）
      gsap.set(this.wrapper, { x: -(this.currentIndex + 1) * this.slideWidth });
    }

    loadPlanetsData() {
      const jsonScript = document.querySelector('#planet-data-json');
      if (jsonScript) {
        try {
          this.planetsData = JSON.parse(jsonScript.textContent);
        } catch {
          this.planetsData = this.config.phase2?.planets || [];
        }
      } else {
        this.planetsData = this.config.phase2?.planets || [];
      }
    }

    bindEvents() {
      const inner = document.querySelector('#seq2-mobile-phase2-inner');
      if (inner) {
        inner.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: true });
        inner.addEventListener('touchend', (e) => this.onTouchEnd(e), { passive: true });
      }
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.goToIndex(this.currentIndex - 1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.goToIndex(this.currentIndex + 1));
      }
    }

    onTouchStart(e) {
      if (this.isAnimating) return;
      if (!e.touches[0]) return;
      this.touchStartX = e.touches[0].clientX;
    }

    onTouchEnd(e) {
      if (this.isAnimating) return;
      if (!e.changedTouches[0]) return;
      const dx = e.changedTouches[0].clientX - this.touchStartX;
      if (Math.abs(dx) < this.swipeThreshold) return;
      if (dx < 0) this.goToIndex(this.currentIndex + 1);
      else this.goToIndex(this.currentIndex - 1);
    }

    goToIndex(targetIndex) {
      // targetIndex 允许 -1（虚拟开头）和 totalSlides（虚拟末尾），用于循环 wrap
      if (this.isAnimating) return;
      if (targetIndex === this.currentIndex) return;

      this.isAnimating = true;

      // 视觉目标 x：targetIndex + 1 的偏移（因为克隆海王星占了第 0 位）
      const visualX = -(targetIndex + 1) * this.slideWidth;

      gsap.to(this.wrapper, {
        x: visualX,
        duration: 0.45,
        ease: 'power3.out',
        onComplete: () => {
          // 边界 wrap：tween 到虚拟槽后，瞬切到真实槽
          if (targetIndex < 0) {
            // 从地球向左到了"虚拟海王星"槽 → 瞬切到真实海王星槽
            this.currentIndex = this.totalSlides - 1;
            gsap.set(this.wrapper, { x: -(this.currentIndex + 1) * this.slideWidth });
          } else if (targetIndex >= this.totalSlides) {
            // 从海王星向右到了"虚拟地球"槽 → 瞬切到真实地球槽
            this.currentIndex = 0;
            gsap.set(this.wrapper, { x: -(this.currentIndex + 1) * this.slideWidth });
          } else {
            this.currentIndex = targetIndex;
          }
          this.isAnimating = false;
        }
      });

      // 数据 / 时间 / 箭头 visibility 立即更新到目标星球（不等动画完成）
      // 目标星球的 planetsData 索引：wrap 之后的真实索引
      const dataIndex = ((targetIndex % this.totalSlides) + this.totalSlides) % this.totalSlides;
      this.updateDisplays(dataIndex, true);
      const planet = this.planetsData[dataIndex];
      if (planet) this.timeController.startTimeUpdate(planet.id);
    }

    updateDisplays(index, animate = true) {
      const planet = this.planetsData[index];
      if (!planet) return;

      const updateText = (el, text) => {
        if (!el) return;

        if (animate) {
          gsap.to(el, {
            yPercent: 100,
            opacity: 0,
            duration: 0.2,
            ease: 'power2.in',
            onComplete: () => {
              el.textContent = text;
              gsap.fromTo(el,
                { yPercent: -100, opacity: 0 },
                { yPercent: 0, opacity: 1, duration: 0.3, ease: 'power2.out' }
              );
            }
          });
        } else {
          el.textContent = text;
          gsap.set(el, { yPercent: 0, opacity: 1 });
        }
      };

      updateText(this.displays.cycle, planet.cycle || '');
      updateText(this.displays.name, planet.name || '');
      updateText(this.displays.lst, planet.lst || '');
      updateText(this.displays.o2, planet.o2 || '');
      updateText(this.displays.grav, planet.grav || '');
      updateText(this.displays.dia, planet.dia || '');
    }
  }

  /**
   * 阶段三控制器 - 序列帧 Scrub + 标题动画
   */
  class Phase3Controller {
    constructor(config, loader) {
      this.config = config;
      this.loader = loader;

      // 帧范围 (0-350，使用独立的帧资源)
      this.totalFrames = config.phase3?.totalFrames || 351;
      this.totalScrollDistance = 0;

      // 复用公共 CanvasRenderer（canvas 不存在时置 null）
      try {
        this.renderer = new CanvasRenderer('#seq2-mobile-phase3-canvas');
      } catch (e) {
        this.renderer = null;
      }

      // 标题元素
      this.titleElements = {};
      this.titleStates = {};  // 跟踪标题显示状态

      this.currentFrame = 0;
      this.lastRenderedFrame = -1;
      this.scrollTrigger = null;
    }

    // 按当前 vh 解析滚动距离；end 函数 + invalidateOnRefresh 在 refresh 时复用
    computeScrollDistance() {
      this.totalScrollDistance = parseScrollHeight(this.config.phase3?.scrollHeight, 4);
      return this.totalScrollDistance;
    }

    initialize() {
      this.computeScrollDistance();

      // 缓存标题元素
      this.cacheTitleElements();

      // 设置 section 高度
      this.setupScrollSpacer();

      // 渲染第一帧
      this.renderFrame(0);

      // resize 时重渲当前帧（CanvasRenderer 自管 resizeCanvas，这里补重渲逻辑）
      viewportHeightManager.onResize(() => {
        if (this.currentFrame >= 0) {
          this.renderFrame(this.currentFrame, true);
        }
      });

      // 初始化标题 (全部隐藏)
      Object.values(this.titleElements).forEach(el => {
        gsap.set(el, { opacity: 0 });
        const lines = el.querySelectorAll('.title-line');
        lines.forEach(line => gsap.set(line, { yPercent: 100 }));
      });

      // 延迟建 pin：交由 YDMobileReady 在首批帧就绪后调用 createScrollTrigger()。
      if (window.YDMobileReady && window.YDMobileReady.active) {
        window.YDMobileReady.need('seq-p3');
        window.YDMobileReady.onRelease(() => this.createScrollTrigger());
      } else {
        this.createScrollTrigger();
      }
    }

    cacheTitleElements() {
      const titles = this.config.phase3?.titles || [];
      for (const titleConfig of titles) {
        if (titleConfig.id) {
          const el = document.querySelector(`#${titleConfig.id}`);
          if (el) {
            this.titleElements[titleConfig.id] = el;
            this.titleStates[titleConfig.id] = 'hidden';
          }
        }
      }
    }

    setupScrollSpacer() {
      // 不再手动设置高度，由 ScrollTrigger 的 pinSpacing 自动处理
    }

    createScrollTrigger() {
      if (typeof ScrollTrigger === 'undefined') {
        return;
      }

      // 使用 pinSpacing: true 让 ScrollTrigger 自动管理间距
      // refreshPriority 6：低于 Phase1(8)，保证多 pin 自上而下刷新
      // end 函数式 + invalidateOnRefresh：横竖屏/大幅视口变化时按新 vh 重算
      this.scrollTrigger = ScrollTrigger.create({
        trigger: '#seq2-mobile-phase3-canvas-wrapper',
        start: 'top top',
        end: () => '+=' + this.computeScrollDistance(),
        pin: '#seq2-mobile-phase3-canvas-wrapper',
        pinSpacing: true,
        scrub: 1,
        refreshPriority: 6,
        invalidateOnRefresh: true,
        onUpdate: (self) => this.handleScrollUpdate(self.progress)
      });
    }

    handleScrollUpdate(progress) {
      // 计算帧号 (0-350，使用独立帧资源)
      const frameNumber = Math.floor(progress * (this.totalFrames - 1));

      // 渲染当前帧
      this.renderFrame(frameNumber);

      // 更新标题
      this.updateTitles(frameNumber);
    }

    renderFrame(frameNumber, forceRender = false) {
      if (frameNumber === this.lastRenderedFrame && !forceRender) return;

      this.currentFrame = frameNumber;

      const img = this.loader.getFrameSafe(frameNumber);
      if (img && this.renderer) {
        this.renderer.renderFrame(img);
      }
      // 只有目标帧真正画上才记录帧号：画的是回退帧或什么都没画时，帧到位后对同帧号的重绘不能被短路
      this.lastRenderedFrame = this.loader.getFrame(frameNumber) ? frameNumber : -1;
    }

    updateTitles(relativeFrame) {
      const titles = this.config.phase3?.titles || [];

      for (const titleConfig of titles) {
        const el = this.titleElements[titleConfig.id];
        if (!el) continue;

        const currentState = this.titleStates[titleConfig.id];
        let targetState = 'hidden';

        // 判断应该显示还是隐藏
        const fadeIn = titleConfig.fadeIn;
        const fadeOut = titleConfig.fadeOut;

        if (fadeIn) {
          if (relativeFrame >= fadeIn.endFrame) {
            // 已完成淡入
            if (fadeOut) {
              if (relativeFrame < fadeOut.startFrame) {
                targetState = 'visible';
              } else if (relativeFrame >= fadeOut.endFrame) {
                targetState = 'hidden';
              } else {
                // 正在淡出中
                targetState = 'fading-out';
              }
            } else {
              // 没有淡出配置，保持显示
              targetState = 'visible';
            }
          } else if (relativeFrame >= fadeIn.startFrame) {
            // 正在淡入中
            targetState = 'fading-in';
          }
        }

        // 执行状态转换
        if (currentState !== targetState) {
          this.transitionTitle(el, titleConfig.id, currentState, targetState);
        }
      }
    }

    transitionTitle(el, id, fromState, toState) {
      const lines = el.querySelectorAll('.title-line');

      if (toState === 'fading-in' || toState === 'visible') {
        if (fromState === 'hidden') {
          // 从隐藏到显示: line-reveal 动画
          gsap.set(el, { opacity: 1 });
          lines.forEach((line, i) => {
            gsap.fromTo(line,
              { yPercent: 100 },
              {
                yPercent: 0,
                duration: 0.5,
                delay: i * 0.1,
                ease: 'power2.out'
              }
            );
          });
        }
        this.titleStates[id] = 'visible';
      } else if (toState === 'fading-out' || toState === 'hidden') {
        if (fromState === 'visible' || fromState === 'fading-in') {
          // 从显示到隐藏: line-reveal 反向动画
          lines.forEach((line, i) => {
            gsap.to(line, {
              yPercent: -100,
              duration: 0.4,
              delay: i * 0.05,
              ease: 'power2.in',
              onComplete: () => {
                if (i === lines.length - 1) {
                  gsap.set(el, { opacity: 0 });
                  // 重置 yPercent 为下次入场准备
                  lines.forEach(l => gsap.set(l, { yPercent: 100 }));
                }
              }
            });
          });
        }
        this.titleStates[id] = 'hidden';
      }
    }

    dispose() {
      if (this.scrollTrigger) {
        this.scrollTrigger.kill();
        this.scrollTrigger = null;
      }
    }
  }

  /**
   * 等待依赖加载
   */
  function waitForDependencies() {
    return new Promise((resolve, reject) => {
      let resolved = false;
      const check = setInterval(() => {
        if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
          clearInterval(check);
          clearTimeout(timer);
          resolved = true;
          resolve();
        }
      }, 100);

      const timer = setTimeout(() => {
        clearInterval(check);
        if (!resolved) {
          reject(new Error('GSAP/ScrollTrigger not loaded within 10s'));
        }
      }, 10000);
    });
  }

  /**
   * 初始化
   */
  async function init() {
    const section = document.querySelector('#product-sequence-section-2-mobile');
    if (!section) {
      return;
    }

    const configData = section.dataset.config;
    if (!configData) {
      return;
    }

    const config = JSON.parse(configData);

    // 初始化视口高度管理器（防止 Safari 地址栏变化引起抖动）
    viewportHeightManager.initialize();

    // 上报 vh 信号：locked-vh 已就绪（>0）即可放行该信号；无 locked-vh 时也不阻塞（回退路径已兜底）
    if (window.YDMobileReady && window.YDMobileReady.active) {
      window.YDMobileReady.need('vh');
      requestAnimationFrame(() => {
        window.YDMobileReady.signal('vh');
      });
    }

    // 等待依赖
    try {
      await waitForDependencies();
    } catch (err) {
      console.warn('[YD ProductSequence 2 Mobile]', err.message);
      // 兜底：释放滚动锁，隐藏 loading，让用户至少能使用页面
      const loadingEl = document.querySelector('#loading-screen');
      if (loadingEl) loadingEl.classList.add('fade-out');
      window.__sequenceScrollRelease?.();
      return;
    }

    try {
      // Phase 1 帧加载器 (帧 0-50，共 51 帧)
      const phase1Loader = new MobileSequenceLoader({
        framePath: config.framePath,
        avifPath: config.avifPath,
        fallbackPath: config.fallbackPath,
        priorityBase: 10000   // 移动端第一套序列（首屏为视频，不占队列）
      });

      // Phase 3 帧加载器 (独立的帧资源 0-350)
      const phase3Config = config.phase3 || {};
      const phase3Loader = new MobileSequenceLoader({
        framePath: phase3Config.framePath || 'https://cf-files.dotnfilm.studio/gravity_web/frames/light_end_mobile/',
        avifPath: phase3Config.avifPath || 'avif/1_[frame].avif',
        fallbackPath: phase3Config.fallbackPath || 'webp/1_[frame].webp',
        priorityBase: 30000   // 排在 phase1 之后
      });

      // 1. 阶段一: 序列帧 - 先创建 ScrollTrigger，后台加载帧
      const renderer = new CanvasRenderer('#seq2-mobile-canvas');
      const phase1 = new Phase1Controller(config, phase1Loader, renderer);
      phase1.initialize();
      phase1Loader.getFocusFrame = () => phase1.currentFrame;

      // 2. 阶段二: 星球滚动切换
      const planets = config.phase2?.planets || [];
      const timeController = new PlanetTimeController(planets);
      timeController.initialize();

      const planetCarousel = new PlanetCarouselController(config, timeController);
      planetCarousel.initialize();

      // 3. 阶段三: 序列帧 Scrub + 标题 - 先创建 ScrollTrigger，后台加载帧
      const phase3 = new Phase3Controller(config, phase3Loader);
      phase3.initialize();
      phase3Loader.getFocusFrame = () => phase3.currentFrame;

      // 存储实例
      window.ydProductSequence2Mobile = {
        phase1,
        phase3,
        planetCarousel,
        timeController,
        phase1Loader,
        phase3Loader
      };

      // 后台加载帧（不阻塞 ScrollTrigger 创建）
      const phase1End = (config.totalFrames || 51) - 1;
      phase1Loader.preloadFrameRange(0, phase1End, config.priorityFrames || 30).then(() => {
        // 帧加载完成后，渲染当前帧
        if (phase1.currentFrame >= 0) {
          phase1.renderFrame(phase1.currentFrame);
        }
        phase1Loader.continuePreload();

        // 首批帧就绪 + canvas 尺寸稳定后上报 seq-p1（pin 在协调器放行时才建）
        whenCanvasStable('#seq2-mobile-canvas', () => {
          // canvas 尺寸已稳定，补一次 resize 确保位图按真实 CSS 盒初始化
          // （首帧若 rect=0 被跳过，此处补救；CSS 盒与锁定值不同即会重算）
          renderer.resizeCanvas();
          if (window.YDMobileReady && window.YDMobileReady.active) {
            window.YDMobileReady.signal('seq-p1');
            // 若此前已降级放行（pin 已用旧尺寸建），补一次 refresh 纠正
            window.YDMobileReady.lateRefresh();
          }
        });
      });

      const phase3TotalFrames = phase3Config.totalFrames || 351;
      const phase3Priority = phase3Config.priorityFrames || 50;
      phase3Loader.preloadFrameRange(0, phase3TotalFrames - 1, phase3Priority).then(() => {
        // 帧加载完成后，渲染当前帧
        if (phase3.currentFrame >= 0) {
          phase3.renderFrame(phase3.currentFrame, true);
        }
        phase3Loader.continuePreload();

        // 与 phase1 对称：等 phase3 canvas 尺寸稳定后再上报 seq-p3
        whenCanvasStable('#seq2-mobile-phase3-canvas', () => {
          if (window.YDMobileReady && window.YDMobileReady.active) {
            window.YDMobileReady.signal('seq-p3');
            window.YDMobileReady.lateRefresh();
          }
        });
      });

    } catch {
      // 初始化失败，静默处理
    }
  }

  // 等待 DOM 加载
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
